import { useQuery } from '@tanstack/react-query';
import { dataService, Measurement, MeasurementStats } from '../services/data';
import { TimeRange } from './useMeasurements';
import type { Period } from '../components/logger/PeriodSelector';

export interface CombinedMeasurements {
  adv: Measurement[];
  official: Measurement[];
  statsAdv: MeasurementStats | null;
  statsOfficial: MeasurementStats | null;
}

const ADV_MAX_DAYS = 7;

function getDateRange(range: TimeRange): { start: string; end: string } {
  const end = new Date();
  const start = new Date();

  switch (range) {
    case '24h':
      start.setHours(start.getHours() - 24);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
  }

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

function isAdvRange(range: TimeRange): boolean {
  return range === '24h' || range === '7d';
}

function isAdvPeriod(period: Period): boolean {
  if (period.preset && period.preset !== 'custom') {
    return isAdvRange(period.preset as TimeRange);
  }
  // custom: show ADV only if range ≤ 7 days
  const diffMs = new Date(period.end).getTime() - new Date(period.start).getTime();
  return diffMs <= ADV_MAX_DAYS * 24 * 60 * 60 * 1000;
}

export function useMeasurementsCombined(
  probeId: string | undefined,
  rangeOrPeriod: TimeRange | Period = '24h'
) {
  // Normalise to { start, end } regardless of input type
  const period: { start: string; end: string } =
    typeof rangeOrPeriod === 'string'
      ? getDateRange(rangeOrPeriod)
      : rangeOrPeriod;

  const fetchAdv =
    typeof rangeOrPeriod === 'string'
      ? isAdvRange(rangeOrPeriod)
      : isAdvPeriod(rangeOrPeriod);

  const queryKey = typeof rangeOrPeriod === 'string'
    ? rangeOrPeriod
    : `${period.start}__${period.end}`;

  const officialQuery = useQuery({
    queryKey: ['measurements_official', probeId, queryKey],
    queryFn: () =>
      dataService.getMeasurementsOfficial(probeId!, period),
    enabled: !!probeId,
    refetchInterval: 60000,
  });

  const advQuery = useQuery({
    queryKey: ['measurements_adv', probeId, queryKey],
    queryFn: () =>
      dataService.getMeasurementsWithStats(probeId!, period),
    enabled: !!probeId && fetchAdv,
    refetchInterval: 60000,
  });

  const combined: CombinedMeasurements = {
    adv: fetchAdv ? (advQuery.data?.items ?? []) : [],
    official: officialQuery.data?.items ?? [],
    statsAdv: fetchAdv ? (advQuery.data?.stats ?? null) : null,
    statsOfficial: officialQuery.data?.stats ?? null,
  };

  return {
    data: combined,
    isLoading: officialQuery.isLoading || (fetchAdv && advQuery.isLoading),
    error: officialQuery.error || advQuery.error,
  };
}

/** Hook for sparkline: last 1h of ADV data for a probe */
export function useSparklineData(probeId: string | undefined) {
  const end = new Date();
  const start = new Date(end.getTime() - 60 * 60 * 1000); // 1 hour ago

  return useQuery({
    queryKey: ['sparkline', probeId],
    queryFn: () =>
      dataService.getMeasurements(probeId!, {
        start: start.toISOString(),
        end: end.toISOString(),
      }),
    enabled: !!probeId,
    refetchInterval: 60000,
  });
}

export { ADV_MAX_DAYS };
